import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  useDownvoteThreadMutation,
  useNeutralizeThreadMutation,
  useUpvoteThreadMutation,
} from '../../store/api/threadsApi';
import {
  asyncAddBookmark,
  asyncDeleteBookmark,
} from '../../store/plain/bookmarks/action';
import { setThreadToReply } from '../../store/slices/replyContextSlice';

import { ContentBody } from '../pieces/ContentBody';
import { ThreadFooter } from '../pieces/ThreadFooter';
import { Header } from './threadExcerpt/Header';

export function ThreadExcerpt({ thread }) {
  const dispatch = useDispatch();
  const { bookmarks = [] } = useSelector((state) => state);
  const { status, currentUser } = useSelector((state) => state.auth);
  const authenticated = status === 'authenticated';

  const [isUpvoted, setIsUpvoted] = useState(false);
  const [isDownvoted, setIsDownvoted] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);

  const [upvoteThread] = useUpvoteThreadMutation();
  const [downvoteThread] = useDownvoteThreadMutation();
  const [neutralizeVote] = useNeutralizeThreadMutation();

  const baseUrl = window.location.origin;
  const sharedData = {
    url: `${baseUrl}/t/${thread.id}`,
    text: `${
      thread.body.length >= 240
        ? thread.body.substring(0, 239).concat('...')
        : thread.body
    }`,
    title: thread.title,
  };

  const onUpvoteButtonClick = () => {
    if (authenticated && isUpvoted) {
      const newUpvotesBy = thread.upVotesBy.filter(
        (voterId) => voterId !== currentUser.id
      );
      neutralizeVote({
        threadId: thread.id,
        newUpvotesBy,
        newDownvotesBy: thread.downVotesBy,
      });
    } else if (authenticated && !isDownvoted) {
      const newUpvotesBy = [...thread.upVotesBy, currentUser.id];
      upvoteThread({ threadId: thread.id, newUpvotesBy });
    } else if (authenticated && isDownvoted) {
      const newDownvotesBy = thread.downVotesBy.filter(
        (voterId) => voterId !== currentUser.id
      );
      const newUpvotesBy = [...thread.upVotesBy, currentUser.id];
      neutralizeVote({ threadId: thread.id, newUpvotesBy, newDownvotesBy });
      upvoteThread({ threadId: thread.id, newUpvotesBy });
    } else {
      document.getElementById('loginDialog').showModal();
    }
  };

  const onDownvoteButtonClick = () => {
    if (authenticated && isDownvoted) {
      const newDownvotesBy = thread.downVotesBy.filter(
        (voterId) => voterId !== currentUser.id
      );
      neutralizeVote({
        threadId: thread.id,
        newUpvotesBy: thread.upVotesBy,
        newDownvotesBy,
      });
    } else if (authenticated && !isUpvoted) {
      const newDownvotesBy = [...thread.downVotesBy, currentUser.id];
      downvoteThread({ threadId: thread.id, newDownvotesBy });
    } else if (authenticated && isUpvoted) {
      const newUpvotesBy = thread.upVotesBy.filter(
        (voterId) => voterId !== currentUser.id
      );
      const newDownvotesBy = [...thread.downVotesBy, currentUser.id];
      neutralizeVote({ threadId: thread.id, newUpvotesBy, newDownvotesBy });
      downvoteThread({ threadId: thread.id, newUpvotesBy, newDownvotesBy });
    } else {
      document.getElementById('loginDialog').showModal();
    }
  };

  const onCommentButtonClick = () => {
    if (authenticated) {
      dispatch(setThreadToReply(thread));
      document.getElementById('addCommentDialog').showModal();
    } else if (!authenticated) {
      document.getElementById('loginDialog').showModal();
    }
  };

  const onBookmarkButtonClick = async () => {
    const result = bookmarks.find(({ id }) => id === thread.id);
    if (result) {
      dispatch(asyncDeleteBookmark(thread.id));
    } else {
      dispatch(asyncAddBookmark(thread));
    }
  };

  const onShareButtonClick = () => {
    if (isSecureContext) {
      navigator.share(sharedData);
    } else {
      alert(
        'You cant do this in unsecured access. Share it from browser address bar instead.'
      );
    }
  };

  useEffect(() => {
    if (currentUser && thread.upVotesBy.includes(currentUser.id)) {
      setIsUpvoted(true);
    }
    if (currentUser && !thread.upVotesBy.includes(currentUser.id)) {
      setIsUpvoted(false);
    }
    if (currentUser && thread.downVotesBy.includes(currentUser.id)) {
      setIsDownvoted(true);
    }
    if (currentUser && !thread.downVotesBy.includes(currentUser.id)) {
      setIsDownvoted(false);
    }
  }, [currentUser, thread]);

  useEffect(() => {
    if (bookmarks) {
      const result = bookmarks.find(({ id }) => id === thread.id);
      if (result) setIsBookmarked(true);
    }
  }, [bookmarks, thread]);

  return (
    <article className="group mx-auto rounded-lg px-2 py-1 lg:max-w-lg xl:max-w-xl">
      <Header
        category={thread.category}
        ownerId={thread.ownerId}
        createdAt={thread.createdAt}
        handleBookmarkButton={onBookmarkButtonClick}
        isBookmarked={isBookmarked}
      />
      <h3>
        <Link
          to={`/t/${thread.id}`}
          state={{ thread }}
          className="mt-3 mb-1 overflow-hidden text-clip break-words text-lg font-semibold opacity-90 line-clamp-2 hover:text-indigo-800 group-hover:opacity-100 dark:hover:text-indigo-200 md:mb-2 lg:text-xl xl:mt-4"
        >
          {thread.title}
        </Link>
      </h3>
      <div className="prose-sm w-full line-clamp-6 prose-code:break-words dark:prose-invert md:prose-base">
        <ContentBody text={thread.body} />
      </div>
      <ThreadFooter
        upvotes={thread.upVotesBy}
        downvotes={thread.downVotesBy}
        isUpvoted={isUpvoted}
        isDownvoted={isDownvoted}
        commentsCount={thread.totalComments}
        handleCommentAction={onCommentButtonClick}
        handleUpvoteAction={onUpvoteButtonClick}
        handleDownvoteAction={onDownvoteButtonClick}
        handleShareAction={onShareButtonClick}
      />
    </article>
  );
}

ThreadExcerpt.propTypes = {
  thread: PropTypes.shape({
    id: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    ownerId: PropTypes.string.isRequired,
    createdAt: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
    upVotesBy: PropTypes.array.isRequired,
    downVotesBy: PropTypes.array.isRequired,
    totalComments: PropTypes.number.isRequired,
  }).isRequired,
};
